import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Mail, CheckCircle, XCircle, Loader2, Home } from 'lucide-react';

type UnsubscribeStatus = 'loading' | 'success' | 'error' | 'invalid';

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<UnsubscribeStatus>('loading');
  const [message, setMessage] = useState('');

  const email = searchParams.get('email');
  const token = searchParams.get('token');

  useEffect(() => {
    const processUnsubscribe = async () => {
      if (!email || !token) {
        setStatus('invalid');
        setMessage('Invalid unsubscribe link. Please use the link from your email.');
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('unsubscribe-newsletter', {
          body: { email, token }
        });

        if (error) {
          console.error('Unsubscribe error:', error);
          setStatus('error');
          setMessage(error.message || 'An error occurred while processing your request.');
          return;
        }

        if (data?.success) {
          setStatus('success');
          setMessage(data.message || 'You have been successfully unsubscribed.');
        } else {
          setStatus('error');
          setMessage(data?.error || 'An error occurred while processing your request.');
        }
      } catch (err: any) {
        console.error('Unsubscribe error:', err);
        setStatus('error');
        setMessage('An error occurred while processing your request. Please try again.');
      }
    };

    processUnsubscribe();
  }, [email, token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/5 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            {status === 'loading' && <Loader2 className="h-8 w-8 text-primary animate-spin" />}
            {status === 'success' && <CheckCircle className="h-8 w-8 text-green-500" />}
            {status === 'error' && <XCircle className="h-8 w-8 text-destructive" />}
            {status === 'invalid' && <Mail className="h-8 w-8 text-muted-foreground" />}
          </div>
          <CardTitle className="text-2xl">
            {status === 'loading' && 'Processing...'}
            {status === 'success' && 'Unsubscribed'}
            {status === 'error' && 'Something Went Wrong'}
            {status === 'invalid' && 'Invalid Link'}
          </CardTitle>
          <CardDescription>
            {status === 'loading' && 'Please wait while we process your request.'}
            {status !== 'loading' && message}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          {status === 'success' && (
            <p className="text-sm text-muted-foreground">
              You will no longer receive newsletter emails from us. 
              We're sorry to see you go!
            </p>
          )}
          
          {status === 'error' && (
            <p className="text-sm text-muted-foreground">
              If this problem persists, please contact our support team.
            </p>
          )}

          <Button asChild className="w-full">
            <Link to="/">
              <Home className="mr-2 h-4 w-4" />
              Return to Home
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
